import { ApolloError } from '@apollo/client';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NAV_PATHS } from '@modules/common/constants';
import { useGetArtefactScope } from '@modules/common/hooks';
import { useUpdateUser } from '@modules/common/hooks/useUpdateUser';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { CustomizeHomeConfirmationModalContent } from '@modules/home/components/HomeDashboard/CustomizeHomeConfirmationModalContent';
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import { useGetUser, useGetUserSetter } from 'v2/core/services/user';
import { useDashboardStore } from '../store/useDashboardStore';
import { UpdateDashboardRequest } from '../types';
import { useCreateDashboard } from './useCreateDashboard';
import { useDuplicateAndUpdateDashboard } from './useDuplicateAndUpdateDashboard';
import { initMetadataV2 } from './useFetchDashboard/helpers/initMetadataV2';
import { useUpdateDashboard } from './useUpdateDashboard';

interface useUpsertDashboardParams {
  shouldSkipRedirect?: boolean;
}
interface upsertDashboardParams {
  dashboardProps: Partial<UpdateDashboardRequest>;
  shouldRefetchAnalyses?: boolean;
  shouldRefetchDashboard?: boolean;
  isAddAnalysisToDashboard?: boolean;
}

export const useUpsertDashboard = ({
  shouldSkipRedirect = false,
}: useUpsertDashboardParams = {}) => {
  const { openModal } = useMuiModalContext();

  const user = useGetUser();
  const navigate = useNavigate();
  const appSetUser = useGetUserSetter();
  const { updateUser } = useUpdateUser();
  const { showErrorNotification } = useNotificationContext();
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const isHomeContext = useDashboardStore(
    ({ visual }) => visual.context === 'Home',
  );
  const isDefaultDashboard =
    user.tenant.dashboardSettings.homepageDashboardID === dashboard.id;
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const { updateDashboard, loading: updateLoading } = useUpdateDashboard();
  const metadata = useDashboardStore(({ metadata }) => metadata);
  const artefactScope = useGetArtefactScope();
  const cellMinimumHeightsByRowID = useDashboardStore(
    ({ visual }) => visual.cellMinimumHeightsByRowID,
  );
  const [duplicateAndUpdateDashboard, { loading: isDuplicateRequestLoading }] =
    useDuplicateAndUpdateDashboard({
      onCompleted: (newDashboard) => {
        if (newDashboard) {
          dispatch({
            type: DashboardActionType.SET_DASHBOARD,
            payload: newDashboard,
          });

          dispatch({
            type: DashboardActionType.SET_VISUAL,
            payload: {
              isUpdateDashboardInProgress: false,
            },
          });

          initMetadataV2({
            dashboard: newDashboard,
            dispatch,
            cellMinimumHeightsByRowID,
          });

          appSetUser({
            ...dependenciesRef.current.user,
            homepageDashboardID: newDashboard.id,
          });
          updateUser({
            homepageDashboardID: newDashboard.id,
          });
        }
      },
    });

  const dependencies = {
    dashboard,
    user,
    isHomeContext,
    metadata,
    artefactScope,
    isDefaultDashboard,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const onCreate = useCallback(
    (dashboard: { viewToken: string }) => {
      if (shouldSkipRedirect) {
        return;
      }

      const dashboardRoute = `/${NAV_PATHS.DASHBOARD}/${dashboard.viewToken}`;
      navigate(dashboardRoute);
    },
    [navigate, shouldSkipRedirect],
  );

  const { createDashboard, loading: createLoading } = useCreateDashboard({
    onCreate,
  });

  const upsertDashboard = useCallback(
    async ({
      dashboardProps: dashboardPropsToBeUpdated,
      shouldRefetchAnalyses,
      isAddAnalysisToDashboard = false,
    }: upsertDashboardParams) => {
      //this variable will be changed to const after contract update
      const dashboard = dependenciesRef.current.dashboard;

      const getSavedDashboard = async () => {
        // Analysis widget mapping doesn't work on createDashboard, so we need to use updateDashboard when adding analysis
        // TODO: Even though there are no side effects of using updateDashboard, this should be fixed in BE (we have been using update call for this for a long time)
        if (!dashboard.id && !isAddAnalysisToDashboard) {
          const { data } = await createDashboard({
            variables: {
              ...dashboardPropsToBeUpdated,
              scope: dependenciesRef.current.artefactScope,
              analysisIDs:
                dashboardPropsToBeUpdated.analysisIDs ??
                dashboardPropsToBeUpdated.addAnalysisIDs,
              title: dashboardPropsToBeUpdated.title ?? dashboard.title ?? '',
              metadata: dashboardPropsToBeUpdated.metadata ?? '',
              shortMetadata:
                dashboardPropsToBeUpdated.shortMetadata ??
                dashboard.shortMetadata,
              tenantID: dependenciesRef.current.user.tenant.id,
              createdBy: dependenciesRef.current.user.id,
            },
          });

          return data?.createDashboard?.dashboard;
        }

        if (
          dependenciesRef.current.isHomeContext &&
          dependenciesRef.current.isDefaultDashboard
        ) {
          openModal({
            contentComponent: () => <CustomizeHomeConfirmationModalContent />,
            modalProps: {
              maxWidth: 'sm',
              fullWidth: true,
            },
          });

          const addAnalysisIDs = dashboardPropsToBeUpdated.addAnalysisIDs
            ? Array.isArray(dashboardPropsToBeUpdated.addAnalysisIDs)
              ? dashboardPropsToBeUpdated.addAnalysisIDs
              : [dashboardPropsToBeUpdated.addAnalysisIDs]
            : undefined;

          const removeAnalysisIDs = dashboardPropsToBeUpdated.removeAnalysisIDs
            ? Array.isArray(dashboardPropsToBeUpdated.removeAnalysisIDs)
              ? dashboardPropsToBeUpdated.removeAnalysisIDs
              : [dashboardPropsToBeUpdated.removeAnalysisIDs]
            : undefined;

          dispatch({
            type: DashboardActionType.SET_VISUAL,
            payload: {
              isUpdateDashboardInProgress: true,
            },
          });

          return await duplicateAndUpdateDashboard({
            duplicateAndUpdateDashboardInput: {
              dashboardID:
                dashboardPropsToBeUpdated.id ??
                dependenciesRef.current.dashboard.id,
              shouldDuplicateAnalyses: true,
              updateParams: {
                addAnalysisIDs,
                removeAnalysisIDs,
                addWidget: dashboardPropsToBeUpdated.addWidget,
                updateWidget: dashboardPropsToBeUpdated.updateWidget,
                deleteWidget: dashboardPropsToBeUpdated.deleteWidget,
                filterClause: dashboardPropsToBeUpdated.filterClause,
                metadataV2: {
                  ...dependenciesRef.current.metadata,
                  ...dashboardPropsToBeUpdated.metadataV2,
                },
              },
            },
            shouldRefetchAnalyses,
          });
        }

        const resp = await updateDashboard({
          updateRequest: {
            ...dashboardPropsToBeUpdated,
            metadataV2: {
              ...dependenciesRef.current.metadata,
              ...dashboardPropsToBeUpdated.metadataV2,
            },
            id: dashboardPropsToBeUpdated.id ?? dashboard.id,
            editToken:
              dashboardPropsToBeUpdated.editToken ?? dashboard.editToken ?? '',
            viewToken:
              dashboardPropsToBeUpdated.viewToken ?? dashboard.viewToken ?? '',
          },
          shouldRefetchAnalyses,
        });

        return resp?.data?.updateDashboard?.dashboard;
      };

      try {
        const updatedDashboard = await getSavedDashboard();

        if (updatedDashboard) {
          dispatch({
            type: DashboardActionType.SET_DASHBOARD,
            payload: updatedDashboard,
          });
        }

        return updatedDashboard;
      } catch (error) {
        if (
          error instanceof ApolloError &&
          isErrorCode(error, ErrorCode.UnprocessableEntity)
        ) {
          return;
        }

        showErrorNotification({
          contentComponent: 'Failed to upsert Dashboard.',
          props: { isClosable: true },
        });
      }
    },
    [
      openModal,
      updateDashboard,
      createDashboard,
      duplicateAndUpdateDashboard,
      dispatch,
      showErrorNotification,
    ],
  );

  return {
    upsertDashboard,
    loading: updateLoading || createLoading || isDuplicateRequestLoading,
  };
};
